import React, { useMemo, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Button
} from '@material-ui/core'
import ColorPickerField from 'components/Form/ColorPickerField'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { toast } from 'utils/notify'
import { useDispatch } from 'react-redux'
import { updateConferenceById } from 'store/conference'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import CustomPopup from 'components/CustomPopup'
import { FirestoreDatabase } from '../../../../index'
import { Collection } from 'constants/index'

// Validation schema for tag form
const tagValidationSchema = yup.object().shape({
  name: yup.string().trim().required('Tag name is required'),
  color: yup.string().required('Tag color is required')
})

// Check if tag name already exists (case insensitive comparison)
const isTagNameDuplicate = (tags, name, currentId = null) => {
  const normalizedName = name.trim().toLowerCase()
  return (tags || []).some(
    (tag) => tag.id !== currentId && tag.name.toLowerCase() === normalizedName
  )
}

// Check if a tag is being used in any events, sessions, or talks
const checkIfTagIsUsed = async (tagId, conferenceId) => {
  try {
    // Check if tag is used in events
    const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .where('tags', 'array-contains', tagId)
      .limit(1)
      .get()

    // Check if tag is used in sessions
    const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('conferenceId', '==', conferenceId)
      .where('tags', 'array-contains', tagId)
      .limit(1)
      .get()

    // Check if tag is used in talks
    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .where('tags', 'array-contains', tagId)
      .limit(1)
      .get()

    return !eventsSnapshot.empty || !sessionsSnapshot.empty || !talksSnapshot.empty
  } catch (error) {
    console.error('Error checking tag usage:', error)
    return false
  }
}

// TagForm Component - Reusable component for creating or editing tags
export const TagForm = ({ tag = {}, onCancel, onSuccess, conference }) => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const tags = conference?.tags || []
  const isEditMode = !!tag?.id

  const initialValues = {
    name: tag?.name || '',
    color: tag?.color || '#1976d2',
    id: tag?.id || ''
  }

  // Save tag (handles both add and update)
  const saveTag = async (tag) => {
    if (!conference?.id) {
      toast.error('Conference ID is missing. Reload and try again.')
      return
    }

    try {
      setIsLoading(true)

      // If editing existing tag
      if (tag?.id && tags.some((t) => t.id === tag.id)) {
        const updatedTags = tags.map((t) => (t.id === tag.id ? tag : t))
        await dispatch(updateConferenceById(conference.id, { tags: updatedTags }))
        toast.success(`Tag "${tag.name}" updated successfully`)
      } else {
        // If adding new tag
        const updatedTags = [...tags, tag]
        await dispatch(updateConferenceById(conference.id, { tags: updatedTags }))
        if (onSuccess) {
          onSuccess(tag?.id)
        }
        toast.success(`Tag "${tag.name}" added successfully`)
      }
      return true
    } catch (error) {
      console.error('Error saving tag:', error)
      toast.error('Could not save tag. Try again.')
      return false
    } finally {
      setIsLoading(false)
      onCancel()
    }
  }

  const handleFormSubmit = async (values, { setFieldError }) => {
    const trimmedName = values.name.trim()
    if (!trimmedName) {
      setFieldError('name', 'Tag name is required')
      return
    }

    if (isTagNameDuplicate(tags, trimmedName, isEditMode ? tag?.id : null)) {
      setFieldError('name', `A tag with name "${trimmedName}" already exists`)
      toast.error(`A tag with name "${trimmedName}" already exists`)
      return
    }

    await saveTag({
      ...values,
      name: trimmedName,
      id: tag?.id || Date.now().toString()
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={tagValidationSchema}
      onSubmit={handleFormSubmit}>
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'rgba(0,0,0,0.54)',
                  marginBottom: '4px',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                }}>
                Tag Name
              </label>
              <TextField
                fullWidth
                size="medium"
                variant="outlined"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter tag name"
                disabled={isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'rgba(0,0,0,0.54)',
                  marginBottom: '4px',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                }}>
                Tag Color
              </label>
              <ColorPickerField
                value={values.color}
                onChange={(color) => setFieldValue('color', color)}
                disabled={isLoading}
              />
              {touched.color && errors.color && (
                <Typography variant="caption" color="error">
                  {errors.color}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Box style={{ display: 'flex', gap: '10px' }}>
                <Button type="button" onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isLoading}>
                  {isLoading ? <CircularProgress size={16} style={{ marginRight: '8px' }} /> : null}
                  {isEditMode ? 'Update Tag' : 'Create Tag'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

const TagsManagementForm = ({ conference }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const tags = useMemo(() => conference?.tags || [], [conference])

  // Popup state
  const [popupOpen, setPopupOpen] = useState(false)
  const [currentTag, setCurrentTag] = useState(null)
  const [deletingTagId, setDeletingTagId] = useState(null)

  const openAddTagPopup = () => {
    setCurrentTag(null)
    setPopupOpen(true)
  }

  const openEditTagPopup = (tag) => {
    setCurrentTag(tag)
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
    setCurrentTag(null)
  }

  // Delete a tag
  const deleteTag = async (tagId) => {
    if (!conference?.id) return

    const tagToDelete = tags.find((tag) => tag?.id === tagId)
    if (!tagToDelete) return

    try {
      setDeletingTagId(tagId)

      // Check if the tag is in use before deleting
      const tagInUse = await checkIfTagIsUsed(tagId, conference.id)
      if (tagInUse) {
        toast.error(`Cannot delete tag "${tagToDelete.name}". It is currently in use.`)
        setDeletingTagId(null)
        return
      }

      const updatedTags = tags.filter((tag) => tag?.id !== tagId)
      await dispatch(updateConferenceById(conference.id, { tags: updatedTags }))
      toast.success(`Tag "${tagToDelete.name}" deleted successfully`)
    } catch (error) {
      toast.error('Could not delete tag. Try again.')
    } finally {
      setDeletingTagId(null)
    }
  }

  const popupTitle = currentTag ? 'Edit Tag' : 'Add New Tag'

  return (
    <Paper className={classes.section}>
      <Box
        className={classes.headerContainer}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
            Tags Management
          </Typography>
        </Box>

        <Button
          startIcon={<AddIcon />}
          color="primary"
          variant="contained"
          size="small"
          onClick={openAddTagPopup}>
          Add Tag
        </Button>
      </Box>

      <Box className={classes.tagsList}>
        {tags && tags.length > 0 ? (
          <div>
            {tags?.map((tag) => (
              <Box key={tag?.id} className={classes.tagItem}>
                <Box className={classes.tagColorIndicator} style={{ backgroundColor: tag.color }} />
                <Typography variant="body1" className={classes.tagName}>
                  {tag?.name}
                </Typography>
                <Box className={classes.tagActions}>
                  <Tooltip title="Edit">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => openEditTagPopup(tag)}
                        disabled={deletingTagId === tag.id}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => deleteTag(tag.id)}
                        disabled={deletingTagId === tag.id}>
                        {deletingTagId === tag.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </div>
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ marginTop: '16px' }}>
            No tags created yet. Click the &quot;Add Tag&quot; button to create your first tag.
          </Typography>
        )}
      </Box>

      {/* Tag Form Popup */}
      <CustomPopup open={popupOpen} title={popupTitle} onClose={closePopup} maxWidth="md">
        <TagForm tag={currentTag} onCancel={closePopup} conference={conference} />
      </CustomPopup>
    </Paper>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  headerContainer: {
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontWeight: 600,
    margin: 0
  },
  subText: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '12px'
  },
  tagsList: {
    marginTop: '16px',
    marginBottom: '24px'
  },
  tagItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    marginBottom: '8px',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #eee'
  },
  tagColorIndicator: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    marginRight: '12px'
  },
  tagName: {
    flex: 1,
    fontWeight: 500
  },
  tagActions: {
    display: 'flex',
    gap: '4px'
  }
})

export default TagsManagementForm
